import { useEffect, useRef, Fragment, useContext } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { useRouter } from 'next/router';
import Head from 'next/head';
import { useTranslation } from 'next-i18next';
import throttle from 'lodash/throttle';

import { EndlessScroll } from '@lib/react-endless-scroll';

import {
  selectIsFetchingFiltersOptions,
  selectColorFiltersOptions,
  selectSizeFiltersOptions,
} from '@selectors/filters';
import {
  selectProductResults,
  selectProductColorVariations,
  selectProductsCount,
  selectProductsHasMore,
  selectIsFetching,
  selectProductsHasPrevious,
} from '@selectors/products';

import { prepareProductsRequestParams } from '@utils/filters';
import { getRouteUrl } from '@utils/urls';
import { breakpointQuery } from '@utils/styled';

import { PRODUCTS_PER_PAGE } from '@common/constants/products';
import { OFFER_TYPES } from '@common/constants/filters';
import useIsomorphicLayoutEffect from '@common/hooks/useIsomorphicLayoutEffect';
import { HOME, PRODUCTS_ALL } from '@common/routes.mjs';
import Product from '@common/containers/Product';
import RouteLayout from '@common/containers/RouteLayout';
import { Filters, FiltersContext } from '@common/components/Filters';
import LoadingIndicator from '@common/components/LoadingIndicator';
import LayoutSwitcher from '@common/components/LayoutSwitcher';
import ProductStickyContainer from '@common/components/ProductStickyContainer';
import { ProductsGrid, ProductWrapper } from '@common/components/ProductsGrid';
import Pagination from '@common/components/Pagination';
import MessageCard from '../MessageCard';
import { useProductListScrollRestore } from '@common/hooks/useProductListScrollRestore';
import { BUTTON_VARIANTS, ButtonLink } from '../Button';
import { FiltersToggleButton } from '../FiltersToggleButton';
import { useMediaQuery } from '@common/hooks/useMediaQuery';

import {
  LoadingContainer,
  ProductsAmount,
  TitleWrapper,
  FirstPageButtonWrapper,
  FirstPageButton,
  Title,
  NoProductMessageWrapper,
  NoProductTitle,
  NoProductText,
} from './ProductsList.styled';

const ProductsList = ({
  title,
  offerType,
  newArrivalOnly,
  publishedOnly,
  startPage,
  setCurrentlyViewedPage,
  fetchNextPage,
  fetchFiltersOptions,
  onPageChange,
}) => {
  const isFrom640up = useMediaQuery(breakpointQuery.from640up);

  const {
    asPath,
    query: { id: categoryId, page = 1, ...query },
  } = useRouter();
  const {
    t,
    i18n: { language },
  } = useTranslation('common');
  const queryPage = parseInt(page || 1, 10);
  const paginationProductsRef = useRef([]);
  const productsGridRef = useRef();

  const productResults = useSelector(selectProductResults);
  const products = useSelector(selectProductColorVariations(productResults));
  const count = useSelector(selectProductsCount);
  const hasMoreProducts = useSelector(selectProductsHasMore);
  const hasPreviousProjects = useSelector(selectProductsHasPrevious);
  const isFetchingProducts = useSelector(selectIsFetching);
  const isFetchingFilters = useSelector(selectIsFetchingFiltersOptions);
  const colorFilterOptions = useSelector(selectColorFiltersOptions);
  const sizeFilterOptions = useSelector(selectSizeFiltersOptions);

  const productsLength = useRef(productResults.length);

  useIsomorphicLayoutEffect(() => {
    productsLength.current = productResults.length;
  }, [query.slug]);

  useProductListScrollRestore(productsGridRef);

  useEffect(() => {
    paginationProductsRef.current = paginationProductsRef.current.slice(
      0,
      Math.ceil(productResults.length / PRODUCTS_PER_PAGE),
    );

    const handleScroll = throttle(() => {
      const { innerHeight } = window;

      const productElementPage = [...paginationProductsRef.current]
        .reverse()
        .findIndex(
          product => product?.getBoundingClientRect().bottom <= innerHeight,
        );

      if (productElementPage === -1) {
        return;
      }

      const newPage =
        startPage +
        Math.abs(productElementPage - paginationProductsRef.current.length + 1);

      setCurrentlyViewedPage(newPage);
    }, 300);

    window.addEventListener('scroll', handleScroll, {
      capture: false,
      passive: true,
    });

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [productResults, startPage, setCurrentlyViewedPage]);

  useEffect(() => {
    fetchFiltersOptions({}, { isInitial: true });
  }, [fetchFiltersOptions]);

  const [pathname, searchParamsFromUrl] = asPath.split('?');
  const basePath = `/${language}${pathname}`;
  const searchParams = new URLSearchParams(
    prepareProductsRequestParams(
      {
        page: queryPage > 1 ? queryPage - 1 : null,
        ...query,
      },
      Object.fromEntries(new URLSearchParams(searchParamsFromUrl).entries()),
    ),
  );
  const prevPageQuery = searchParams.toString().replace(/^./, '?$&');

  searchParams.set('page', queryPage + 1);

  const nextPageQuery = searchParams.toString().replace(/^./, '?$&');
  const totalPages = Math.ceil(count / PRODUCTS_PER_PAGE);

  const isQueryPageInRange = queryPage > 0 && queryPage <= totalPages;

  const isFirstPageButtonVisible =
    startPage > 1 && hasPreviousProjects && isQueryPageInRange;

  const changePageHistory = pageNum => {
    if (window?.history) {
      searchParams.set('page', pageNum);
      const pageUrl = `${basePath}${searchParams.toString().replace(/^./, '?$&')}`;

      window.history.replaceState(
        {
          ...window.history.state,
          as: pageUrl,
          url: pageUrl,
        },
        '',
        basePath,
      );
    }
  };

  const filters = useContext(FiltersContext);

  return (
    <>
      <Head>
        {queryPage > 1 && (
          <link
            rel="prev"
            href={`${basePath}${prevPageQuery}`}
          />
        )}
        {hasMoreProducts && (
          <link
            rel="next"
            href={`${basePath}${nextPageQuery}`}
          />
        )}
      </Head>

      <RouteLayout
        backRoute={HOME}
        backRouteAs={getRouteUrl(language, HOME)}
        columns={products.length ? 12 : 8}
        stickyTitle={title}
        stickyTitleColumns={!products.length && 12}
        additionalControls={
          productsLength.current > 0 &&
          isFrom640up && (
            <FiltersToggleButton
              onClick={filters.openFiltersModal}
              isFiltered={filters.isFiltered}
              filtersCount={filters.allFiltersCount}
              shouldRenderExternally
            />
          )
        }
      >
        <TitleWrapper>
          <Title
            variant="h2"
            customAs="h1"
          >
            {title}
          </Title>
          {isQueryPageInRange && (
            <ProductsAmount>
              <span data-testid="productsAmount">{count}</span>
            </ProductsAmount>
          )}
        </TitleWrapper>

        {isFirstPageButtonVisible && (
          <FirstPageButtonWrapper>
            <FirstPageButton
              onClick={() => onPageChange(1)}
              data-testid="showAll"
            >
              {t(
                'productsList.viewPreviousProducts',
                'Vorherige Produkte anzeigen',
              )}
            </FirstPageButton>
          </FirstPageButtonWrapper>
        )}

        {!products.length && (
          <>
            <MessageCard>
              {t('productsList.noProductMessage', 'Nichts gefunden')}
            </MessageCard>
            <NoProductMessageWrapper>
              <NoProductTitle>
                {t('productsList.noProductTitle', 'Produkte entdecken')}
              </NoProductTitle>
              <NoProductText>
                {t(
                  'productsList.noProductText',
                  'Schaue täglich vorbei, um nichts zu verpassen und von allen Vorteilen zu profitieren. Sieh Dir in der Zwischenzeit unser Sortiment an.',
                )}
              </NoProductText>
              <ButtonLink
                variant={BUTTON_VARIANTS.LEVEL_1_GREEN}
                href={getRouteUrl(language, PRODUCTS_ALL)}
              >
                {t('productsList.noProductButton', 'Jetzt entdecken')}
              </ButtonLink>
            </NoProductMessageWrapper>
          </>
        )}

        <EndlessScroll
          hasMore={hasMoreProducts}
          onReachBottom={fetchNextPage}
          isLoading={isFetchingProducts}
        >
          <div>
            <ProductsGrid ref={productsGridRef}>
              {products.map(
                (
                  {
                    price,
                    specialPrice,
                    title: productTitle,
                    offerType: productOfferType,
                    image,
                    productColorId,
                    slug,
                    availability,
                    publishedUntil,
                    productId,
                    colorTitle,
                  },
                  index,
                ) => (
                  <ProductWrapper
                    key={`${productColorId}-${index}`} // eslint-disable-line react/no-array-index-key
                    ref={
                      !(index % PRODUCTS_PER_PAGE)
                        ? el => {
                            paginationProductsRef.current[
                              index / PRODUCTS_PER_PAGE
                            ] = el;
                          }
                        : null
                    }
                    data-id={productColorId}
                  >
                    <Product
                      availability={availability}
                      publishedUntil={publishedUntil}
                      image={image}
                      offerType={productOfferType}
                      price={price}
                      productColorId={productColorId}
                      slug={slug}
                      specialPrice={specialPrice}
                      title={productTitle}
                      productId={productId}
                      colorTitle={colorTitle}
                      onProductClick={() =>
                        changePageHistory(
                          parseInt(
                            index / PRODUCTS_PER_PAGE + parseInt(startPage, 10),
                            10,
                          ),
                        )
                      }
                    />
                  </ProductWrapper>
                ),
              )}
            </ProductsGrid>
            <ProductStickyContainer productsGridRef={productsGridRef}>
              <div />
              {count > 0 && <LayoutSwitcher />}
              {productsLength.current > 0 && (
                <Filters
                  isFetching={isFetchingFilters}
                  colorOptions={colorFilterOptions}
                  sizeOptions={sizeFilterOptions}
                  category={categoryId}
                  newArrivalOnly={newArrivalOnly}
                  offerType={offerType}
                  publishedOnly={publishedOnly}
                  fetchFiltersOptions={fetchFiltersOptions}
                  shouldRenderExternalToggleButton={isFrom640up}
                />
              )}
            </ProductStickyContainer>
          </div>

          {isFetchingProducts && (
            <LoadingContainer data-testid="loader">
              <LoadingIndicator isLarge />
            </LoadingContainer>
          )}
        </EndlessScroll>

        {totalPages > 1 && isQueryPageInRange && (
          <Pagination
            currentPage={queryPage}
            totalPages={totalPages}
            onPageChange={onPageChange}
          />
        )}
      </RouteLayout>
    </>
  );
};

ProductsList.propTypes = {
  newArrivalOnly: PropTypes.bool,
  offerType: PropTypes.oneOf(Object.keys(OFFER_TYPES)),
  publishedOnly: PropTypes.bool,
  title: PropTypes.string.isRequired,
  startPage: PropTypes.number.isRequired,
  setCurrentlyViewedPage: PropTypes.func.isRequired,
  fetchNextPage: PropTypes.func.isRequired,
  fetchFiltersOptions: PropTypes.func.isRequired,
  onPageChange: PropTypes.func.isRequired,
};

ProductsList.defaultProps = {
  newArrivalOnly: false,
  offerType: undefined,
  publishedOnly: true,
};

export default ProductsList;
